import { endOfMonth, format, isValid, parse, parseISO } from 'date-fns'
import type { Patient, PatientWithOptionalId } from '../../types'
import { trimValue } from '../text-formatter'

export type CreatePatientRequest = Omit<Patient, 'id'>

export type BatchCreatePatientsResponseItem = {
  data: {
    id: string
  } | null
  error: {
    reason: any
  } | null
}

export const emptyPatient: PatientWithOptionalId = {
  given_names: '',
  family_name: '',
  gender: 'N',
  date_of_birth: '',
  ihi_number: '',
  ihi_status: '',
  record_status: '',
  entitlement_no: '',
  ctg_flag: false,
  hospital_category: false,
  pbs_entitlement_no: '',
  pbs_safety_net_entitlement_card_holder: false,
  pbs_concession_card_holder: false,
  email: '',
  phone_number: '',
  state: '',
  suburb: '',
  address_1: '',
  address_2: '',
  postcode: 0,
  medicare_no: '',
  medicare_irn: '',
  medicare_valid_to: '',
  dva_file_no: '',
  dva_card_colour: '',
  concession_pension_no: '',
  atsi_status: '',
  paper_only: false,
}

const t = (v: string | null | undefined) => trimValue(v ?? undefined) ?? ''

export function normalizePatientFormData<T extends CreatePatientRequest | Patient>(data: T): T {
  const d = { ...data }

  d.given_names = t(d.given_names)
  d.family_name = t(d.family_name)

  d.gender = t(d.gender)
  d.date_of_birth = t(d.date_of_birth)
  d.medicare_no = t(d.medicare_no)
  d.medicare_irn = t(d.medicare_irn)
  d.medicare_valid_to = t(d.medicare_valid_to)
  d.dva_file_no = t(d.dva_file_no)
  d.dva_card_colour = t(d.dva_card_colour)
  d.ihi_number = t(d.ihi_number)
  d.ihi_status = t(d.ihi_status)
  d.record_status = t(d.record_status)
  d.pbs_entitlement_no = t(d.pbs_entitlement_no)
  d.email = t(d.email)
  d.phone_number = t(d.phone_number)
  d.address_1 = t(d.address_1)
  d.address_2 = t(d.address_2)
  d.suburb = t(d.suburb)
  d.state = t(d.state)
  d.entitlement_no = t(d.entitlement_no)
  d.concession_pension_no = t(d.concession_pension_no)

  d.postcode = (
    d.postcode === null || d.postcode === undefined
      ? 0
      : parseInt(String(d.postcode).trim(), 10) || 0
  ) as T['postcode']

  d.atsi_status = d.atsi_status == null ? d.atsi_status : t(d.atsi_status)
  d.racfid = d.racfid == null ? d.racfid : t(d.racfid)
  d.hospital_provider_number =
    d.hospital_provider_number == null ? d.hospital_provider_number : t(d.hospital_provider_number)
  d.pms_id = d.pms_id == null ? d.pms_id : t(d.pms_id)

  if ('id' in d && d.id) {
    d.id = t(d.id)
  }

  return d
}

export function medicareValidToToMonthInputValue(stored: string | undefined): string {
  if (!stored?.trim()) return ''
  const s = stored.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const parsedDate = parse(s, 'yyyy-MM-dd', new Date())
    if (!isValid(parsedDate) || format(parsedDate, 'yyyy-MM-dd') !== s) return ''
    return format(parsedDate, 'yyyy-MM')
  }
  if (/^\d{4}-\d{2}$/.test(s)) return s
  const d = parseISO(s)
  if (!isValid(d)) return ''
  if (s.includes('T') || s.includes(' ')) return format(d, 'yyyy-MM')
  return format(d, 'yyyy-MM-dd') === s ? format(d, 'yyyy-MM') : ''
}

export function expandYearMonthMedicareValidToEndOfDay(ym: string): string | null {
  if (!/^\d{4}-\d{2}$/.test(ym)) return null
  const parsedDate = parse(`${ym}-01`, 'yyyy-MM-dd', new Date())
  if (!isValid(parsedDate)) return null
  return format(endOfMonth(parsedDate), 'yyyy-MM-dd')
}

export const transformPatientRequestPayload = <T>(data: Patient | CreatePatientRequest): T => {
  const { medicare_valid_to } = data
  if (medicare_valid_to && medicare_valid_to.match(/^\d{4}-\d{2}$/)) {
    const expanded = expandYearMonthMedicareValidToEndOfDay(medicare_valid_to)
    data.medicare_valid_to = expanded ?? medicare_valid_to
  }

  return data as unknown as T
}
